import type { IAssetService } from "@iventis/components";
import type { Asset } from "@iventis/domain-model/model/asset";
import { AssetType } from "@iventis/domain-model/model/assetType";
import { Status } from "@iventis/domain-model/model/status";
import type { PagedResult } from "@iventis/domain-model/model/pagedResult";
import { AssetCategory } from "@iventis/domain-model/model/assetCategory";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { FilterFormat } from "@iventis/types/api.types";
import type { AxiosResponse, AxiosRequestConfig } from "axios";
import { AssetManagement } from "@iventis/domain-model/model/assetManagement";
import type { Model } from "@iventis/domain-model/model/model";
import type { CreateAssetRequest } from "@iventis/domain-model/model/createAssetRequest";
import {
    AssetsPostRequest,
    convertModelDataToThumbnailAssetPostData,
    convertModelDataToLodFileAssetPostData,
    convertUploadModelDataToModelPostData,
    convertUploadIconDataToAssetPostData,
    convertUploadIconDataToAssetPatchData,
    constructModelAssetMetadata,
    convertUploadModelImageDataToAssetPostData,
} from "@iventis/api-helpers";
import { assetsApi, cdnAssets, mappingApi } from "@iventis/api/src/api";
import { multipleModelsGetter } from "@iventis/api-helpers-mapping";
import { UploadIconData, UploadModelData, UploadModelImageData } from "@iventis/types/model-types";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { toast } from "@iventis/toasts/src/toast";
import { getChangedModelData } from "./asset-service-helpers";

/** Api calls needed to use the asset selectors in the main application */
export const assetService: IAssetService = {
    getAsset: async (assetId: string, suppressErrors = false) => {
        const { data } = await assetsApi.get<Asset>(`/assets/${assetId}`, { suppressErrors });
        return data;
    },
    getAssetsById: async (ids) => {
        const data = await Promise.all(
            ids.map(async (assetId) => {
                const res = await assetsApi.get<Asset>(`/assets/${assetId}`);
                return res.data;
            })
        );
        return data;
    },
    getAssetsByType: async (pageNumber, assetType, filters = []) => {
        const statusFilter: FilterFormat = { fieldName: "status", operator: IventisFilterOperator.Eq, value: Status.Active };
        const filtersWithoutStatus = (filters ?? []).filter((f) => f.fieldName !== "status");
        const response = await assetsApi.post<PagedResult<Asset[]>>(`/assets/filter?pageNumber=${pageNumber}&assetType=${assetType}`, [statusFilter, ...filtersWithoutStatus]);
        return response.data;
    },
    getAssetTags: async (tagType: AssetType) => {
        const res = await assetsApi.get<Hierarchy<AssetCategory>[]>(`/tags?assetType=${tagType}`);
        return res.data;
    },
    getCdnAsset: async <T>(url: string) => {
        const res = await cdnAssets.get<T>(url);
        return res.data;
    },
    postIcon: async (data: UploadIconData) => {
        const params: AxiosRequestConfig<AssetsPostRequest> = {
            params: { height: data?.image?.height, width: data?.image?.width },
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.map8.upload_asset.upload_icon)?.toLocaleLowerCase?.() }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.map8.upload_asset.upload_icon) }),
            },
        };
        const res = await assetsApi
            .post<Asset, AxiosResponse<Asset>, AssetsPostRequest>(
                "assets",
                {
                    ...convertUploadIconDataToAssetPostData(data),
                    management: AssetManagement.Internal,
                },
                params
            )
            .then((res) => res.data);
        return res;
    },
    patchIcon: async (data: UploadIconData, existingAsset: Asset) => {
        const res = await assetsApi
            .patch<Asset, AxiosResponse<Asset>>(
                `assets/${existingAsset.id}`,
                {
                    ...convertUploadIconDataToAssetPatchData(data, existingAsset),
                    management: AssetManagement.Internal,
                    height: data?.image?.height,
                    width: data?.image?.width,
                },
                {
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.map8.upload_asset.update_icon)?.toLocaleLowerCase?.() }),
                        message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.map8.upload_asset.update_icon) }),
                    },
                }
            )
            .then((res) => res.data);
        return res;
    },
    postModel: async (postModelData: UploadModelData): Promise<Model & { thumbnail: Asset }> => {
        try {
            // First upload the thumbnail to assets.
            const createthumbnailAsset = convertModelDataToThumbnailAssetPostData(postModelData);
            const thumbnailAsset = await createAsset(createthumbnailAsset);

            // Upload the files to assets (Currently we only have 1 file)
            const fileToCreate = convertModelDataToLodFileAssetPostData(postModelData);
            const fileAsset = await createAsset(fileToCreate);

            const modelToCreate = convertUploadModelDataToModelPostData(postModelData, thumbnailAsset.id, fileAsset.id);

            // call mapping service
            const model = await mappingApi.post<Model, AxiosResponse<Model>, Model>("models", modelToCreate).then((res) => res.data);

            return { ...model, thumbnail: thumbnailAsset };
        } catch (err) {
            toast.error({
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.model) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.model) }),
            });
            throw err;
        }
    },
    updateModel: async (updated: UploadModelData, original: UploadModelData): Promise<Model> => {
        try {
            const updatedModelData = getChangedModelData(updated, original);
            // Get the model with the LoD asset id
            const [modelWithLodFiles] = await multipleModelsGetter([updatedModelData.modelId], mappingApi);
            // Update model properties
            const updatedModel: Model = {
                id: updatedModelData.modelId,
                name: updatedModelData.name ?? modelWithLodFiles.name,
                height: updatedModelData.size?.height ?? modelWithLodFiles.height,
                width: updatedModelData.size?.width ?? modelWithLodFiles.width,
                length: updatedModelData.size?.length ?? modelWithLodFiles.length,
                imageAspectRatioHeight: updatedModelData?.aspectRatio?.height ?? modelWithLodFiles?.imageAspectRatioHeight,
                imageAspectRatioWidth: updatedModelData?.aspectRatio?.width ?? modelWithLodFiles?.imageAspectRatioWidth,
                categories: updatedModelData.categories ?? modelWithLodFiles.categories,
                thumbnailAssetId: updatedModelData.thumbnailId,
                lods: [],
            };

            // If model or categories have changed update the model Asset
            if (updatedModelData.model || updatedModelData.categories) {
                // We only support a single file for each model
                const lodAssetId = modelWithLodFiles.lods[0].files[0].assetId;
                await assetsApi.patch<void, AxiosResponse<void>, PatchModelLodAsset>(
                    `assets/${lodAssetId}`,
                    {
                        dataUrl: updatedModelData.model?.modelUrl,
                        tags: updatedModelData.categories,
                    },
                    { suppressErrors: true }
                );
            }

            // If categories, thumbnail, name or metadata has changed update the model thumbnail
            if (
                updatedModelData.categories ||
                updatedModelData.thumbnail ||
                updatedModelData.name ||
                updatedModelData.variableSizeModel != null ||
                updatedModelData.customImage != null ||
                updatedModelData.customColour != null
            ) {
                await assetsApi.patch<void, AxiosResponse<void>, PatchModelThumbnailAsset>(
                    `assets/${updatedModelData.thumbnailId}`,
                    {
                        tags: updatedModelData.categories,
                        name: updatedModelData.name,
                        metaData: constructModelAssetMetadata(updatedModelData, original),
                        dataUrl: updatedModelData.thumbnail?.imageUrl,
                    },
                    { suppressErrors: true }
                );
            }

            // If name or model size has changed update the model in the mapping service
            if (updatedModelData.name || updatedModelData.size || updatedModelData.variableSizeModel || updatedModelData.customImage || updatedModelData.aspectRatio) {
                await mappingApi.put<void, AxiosResponse<void>, Pick<Model, "id" | "name" | "height" | "length" | "width" | "imageAspectRatioHeight" | "imageAspectRatioWidth">>(
                    "models",
                    updatedModel,
                    { suppressErrors: true }
                );
            }

            return updatedModel;
        } catch (err) {
            toast.error({
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.model) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.model) }),
            });
            throw err;
        }
    },
    assetPagination: true,
    canEditAsset: () => undefined,
    deleteAsset: async (assetId: string) =>
        assetsApi.delete(`/assets/${assetId}`, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.map7.assets.delete) }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.map7.assets.delete) }),
            },
        }),
    getRecommendedCategories: async (assetType: AssetType) => {
        const response = await assetsApi.get(`recommended-tags?assetType=${assetType}`);
        return response.data;
    },
    postModelImage: async (data: UploadModelImageData) => {
        const params: AxiosRequestConfig<AssetsPostRequest> = {
            params: { height: data?.image?.height, width: data?.image?.width },
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.map7.assets.uploadAsset)?.toLocaleLowerCase?.() }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.map7.assets.uploadAsset) }),
            },
        };
        const res = await assetsApi
            .post<Asset, AxiosResponse<Asset>, AssetsPostRequest>(
                "assets",
                {
                    ...convertUploadModelImageDataToAssetPostData(data),
                    management: AssetManagement.Internal,
                },
                params
            )
            .then((res) => res.data);
        return res;
    },
};

export const createAsset = async (asset: AssetsPostRequest) =>
    assetsApi
        .post<Asset, AxiosResponse<Asset>, AssetsPostRequest>("assets", { ...asset, management: AssetManagement.Internal })
        .then((res) => res.data);

type PatchModelLodAsset = Partial<Pick<CreateAssetRequest, "tags" | "dataUrl">>;

type PatchModelThumbnailAsset = Partial<Pick<CreateAssetRequest, "name" | "tags" | "metaData" | "dataUrl">>;
